import { defineEntityConstellationDerivation } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { EntityConstellationDerivation } from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    toDirectoryChildCollectionKey,
    toDirectoryConstellationKey,
    toNodeItemKey,
    toRootCollectionKey,
    toRootConstellationKey,
} from '@/adapters/core/_pure/keyCodec';
import type { Config } from '@/adapters/core/_contract/config';
import type { VisualNode } from '@/adapters/core/_pure/projection';
import type { CoreLayout } from '@/adapters/core/_pure/derivation/coreLayout';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';

export function toDirectoryConstellationDerivations(
    nodes: readonly VisualNode[],
    config: Config,
    layout: CoreLayout,
    rotationByPath?: ReadonlyMap<string, Vector3Tuple>,
    explicitPositions = false,
): EntityConstellationDerivation[] {
    const rootNode = nodes.find((node) => node.parentPath === null);
    const rootConstellation = rootNode === undefined
        ? []
        : [defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toRootConstellationKey(),
                itemCollectionKey: toRootCollectionKey(),
                anchor: {
                    itemKey: toNodeItemKey(rootNode.path),
                    anchorKey: 'center',
                },
            },
            parametricState: {
                layout: {
                    layoutKind: 'ring',
                    anchor: { anchorKind: 'origin' },
                    radius: 0,
                },
                ringGuide: false,
                labelChannelPolicy: { aboveItem: 'always' },
            },
        })];

    const childConstellations = nodes.flatMap((node) => {
        if (node.nodeKind !== 'directory' || node.childPaths.length === 0) return [];
        const ringCenter = explicitPositions ? layout.entryByPath.get(node.path)?.position : layout.ringCenterByPath.get(node.path);
        if (ringCenter === undefined) return [];

        return [defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toDirectoryConstellationKey(node.path),
                itemCollectionKey: toDirectoryChildCollectionKey(node.path),
                anchor: {
                    itemKey: toNodeItemKey(node.path),
                    anchorKey: 'center',
                },
            },
            parametricState: {
                layout: {
                    layoutKind: 'ring',
                    anchor: { anchorKind: 'position', position: ringCenter },
                    radius: explicitPositions ? 0 : layout.ringRadiusByPath.get(node.path) ?? 0,
                    ...(rotationByPath?.has(node.path) ? { rotation: rotationByPath.get(node.path)! } : {}),
                },
                ringGuide: explicitPositions ? false : {
                    color: node.boundaryKind !== null
                        ? config.visualStyle.boundaryColors[node.boundaryKind]
                        : config.visualStyle.directoryColor,
                    opacity: node.depth <= 1 ? 0.32 : node.depth <= 3 ? 0.18 : 0.09,
                    width: node.depth <= 1 ? 0.1 : node.depth <= 3 ? 0.05 : 0.025,
                },
                labelChannelPolicy: {
                    aboveItem: config.showLabelsAtAllLevels || node.depth <= 3 ? 'always' : 'hover',
                },
            },
        })];
    });

    return [...rootConstellation, ...childConstellations];
}